'use client'

import { useEffect, useState } from 'react'
import { Sparkles, Loader2 } from 'lucide-react'
import { cn } from '@/lib/utils'

interface ActivityState {
  label: string
  index: number
  total: number
}

export function SamanthaActivityHeader({ chatOpen }: { chatOpen: boolean }) {
  const [state, setState] = useState<ActivityState | null>(null)

  useEffect(() => {
    const handler = (event: Event) => {
      const detail = (event as CustomEvent).detail as {
        active: boolean
        label?: string
        index?: number
        total?: number
      }

      if (!detail?.active) {
        setState(null)
        return
      }

      setState({
        label: detail.label || 'Samantha está trabajando…',
        index: detail.index ?? 0,
        total: detail.total ?? 0,
      })
    }

    window.addEventListener('samantha:activity', handler)
    return () => window.removeEventListener('samantha:activity', handler)
  }, [])

  if (chatOpen || !state) return null

  const pct =
    state.total > 0 ? Math.round(((state.index + 1) / state.total) * 100) : 0

  return (
    <div
      className={cn(
        'fixed top-2 left-1/2 -translate-x-1/2 z-[60]',
        'flex items-center gap-2 px-3 py-1.5 rounded-full',
        'bg-white/95 dark:bg-neutral-950/95 backdrop-blur border border-neutral-200 dark:border-neutral-800 shadow-lg',
        'animate-in fade-in slide-in-from-top-2 duration-300'
      )}
      role="status"
      aria-live="polite"
    >
      <div className="relative h-5 w-5 rounded-full bg-black dark:bg-white flex items-center justify-center">
        <Sparkles className="h-3 w-3 text-white dark:text-black" />
      </div>

      <Loader2 className="h-3 w-3 animate-spin text-black dark:text-white" />

      <span className="text-xs font-medium text-neutral-900 dark:text-neutral-100 max-w-[260px] truncate">
        {state.label}
      </span>

      {state.total > 0 && (
        <span className="text-[10px] text-neutral-500 dark:text-neutral-400 tabular-nums">
          {state.index + 1}/{state.total} · {pct}%
        </span>
      )}
    </div>
  )
}